"use client";

import { Loader2, AlertCircle, ShieldCheck, CreditCard, Building2, Share2, Copy, Check, FileText, ChevronDown, ChevronUp, X } from "lucide-react";
import { Link } from "@/src/lib/next-compat";
import * as api from "@/src/api/sdk.gen";
import { client } from "@/src/api/client.gen";
import type { BillingCountrySchema } from "@/src/api/types.gen";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useUser } from "@clerk/clerk-react";
import Script from "@/src/lib/next-script-compat";
import { useCallback, useEffect, useMemo, useState } from "react";
import { validateName, validateEmail, validateAddress, validateCity } from "@/lib/validation";
import { fmtCurrency, fmtDisplayPrice } from "@/lib/checkout-formatters";

/**
 * The country and region table, fetched from the API rather than duplicated here.
 *
 * The same rules decide whether `payments/initiate` accepts the address, so a second copy in
 * TypeScript would eventually offer the buyer a region the server rejects.
 */
type BillingCountry = BillingCountrySchema;

/**
 * Which HyperPay widget to load — their sandbox or their live one.
 *
 * Read through `import.meta.env`, because vite.config sets `define: {'process.env': {}}`,
 * so the old `process.env.NEXT_PUBLIC_*` lookup compiled to `undefined` and this was pinned
 * to eu-test in every environment. A production build would have talked to live HyperPay
 * from the server while loading the sandbox widget in the browser, and no card would have
 * gone through. Defaults to the sandbox: getting test money wrong is recoverable.
 */
const PAYMENT_WIDGET_SCRIPT_BASE =
  (import.meta.env?.VITE_HYPERPAY_WIDGET_URL as string | undefined) ??
  "https://eu-test.oppwa.com/v1/paymentWidgets.js";

type PayFastHandoff = { action: string; fields: Record<string, string> };

type InitiateResult = { checkoutId?: string; redirectUrl?: string; payfast?: PayFastHandoff };

/** Coalesce parallel initiate calls (e.g. React Strict Mode double effect) to one request. */
const initiateCheckoutByPlanId = new Map<string, Promise<InitiateResult>>();

/**
 * PayFast takes the buyer to its own page by form POST, so the browser has to submit one — a
 * fetch would receive the page instead of navigating to it.
 */
function submitToPayFast({ action, fields }: PayFastHandoff) {
  const form = document.createElement("form");
  form.method = "POST";
  form.action = action;
  for (const [name, value] of Object.entries(fields)) {
    const input = document.createElement("input");
    input.type = "hidden";
    input.name = name;
    input.value = value;
    form.appendChild(input);
  }
  document.body.appendChild(form);
  form.submit();
}

/** Spaces, dashes and a +92 / 0092 prefix are how people write a Pakistani number; the API
 *  normalises the same way and has the final say. */
function isValidPakistaniMobile(value: string): boolean {
  const compact = value.replace(/[\s-]/g, "").replace(/^(\+92|0092)(?=3\d{9}$)/, "0");
  return /^03\d{9}$/.test(compact);
}

export default function CheckoutPlanClient({
  planId,
  tierId,
  packageId,
  orgSlug,
  wsSlug,
}: {
  planId?: string;
  tierId?: string;
  packageId?: string;
  orgSlug?: string;
  wsSlug?: string;
}) {
  const { user } = useUser();
  const [checkoutId, setCheckoutId] = useState<string | null>(null);
  const [loading, setLoading] = useState(false); // payment initiation (HyperPay) loading
  const [error, setError] = useState<string | null>(null); // payment initiation error
  const [voucherInput, setVoucherInput] = useState("");
  const [appliedVoucherCode, setAppliedVoucherCode] = useState<string | null>(null);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const isTier = Boolean(tierId && /^\d+$/.test(tierId));
  const isPlan = Boolean(planId && /^\d+$/.test(planId));
  const isPackage = Boolean(packageId && packageId.trim().length > 0);

  const initialCompany = searchParams.get("company") || "";
  const initialVat = searchParams.get("vat") || "";
  const initialCr = searchParams.get("cr") || "";
  const initialPo = searchParams.get("po") || "";
  const initialAddon = searchParams.get("addon") === "bundle_100";

  const [includeCreditsAddon, setIncludeCreditsAddon] = useState(initialAddon);

  // The nine fields HyperPay requires. Their integration mail is explicit that a checkout
  // missing any of them is rejected, so the shape here is theirs, not ours.
  const [billingForm, setBillingForm] = useState({
    givenName: "",
    surname: "",
    email: "",
    country: "SA",
    street1: "",
    city: "",
    state: "",
    postcode: "",
    /** Only asked for where the country's gateway needs it (Pakistan, for PayFast's OTPs). */
    phone: "",
    isBusinessPurchase: Boolean(isTier || isPackage || initialCompany || initialVat),
    companyName: initialCompany,
    companyVatNumber: initialVat,
    companyCrNumber: initialCr,
    purchaseOrderNumber: initialPo,
  });

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [shareCopied, setShareCopied] = useState(false);

  const [touched, setTouched] = useState<Record<string, boolean>>({});

  const [countries, setCountries] = useState<BillingCountry[] | null>(null);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      try {
        const { data } = await api.getBillingRegionsApiV1PaymentsBillingRegionsGet();
        if (!data || cancelled) return;
        setCountries(data.countries);
      } catch {
        // Leaves the country and region selects empty; the buyer sees the fields disabled
        // rather than a form that silently accepts an address the API will refuse.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Pre-fill from the signed-in account. Clerk splits the name for us, which is what the
  // gateway wants — it takes givenName and surname separately, not one full name.
  useEffect(() => {
    if (user) {
      setBillingForm((prev) => ({
        ...prev,
        givenName: prev.givenName || user.firstName || "",
        surname: prev.surname || user.lastName || "",
        email: prev.email || user.primaryEmailAddress?.emailAddress || "",
      }));
    }
  }, [user]);

  const currentCountry = useMemo(
    () => countries?.find((c) => c.code === billingForm.country) ?? null,
    [countries, billingForm.country],
  );

  // Real-time validations. These mirror the server's rules closely enough to catch typos as
  // they happen; the server is what actually decides, and its message wins on submit.
  const givenNameValidation = validateName(billingForm.givenName, "First name", touched.givenName);
  const surnameValidation = validateName(billingForm.surname, "Last name", touched.surname);
  const emailValidation = validateEmail(billingForm.email, touched.email);
  const addressValidation = validateAddress(billingForm.street1, touched.street1);
  const cityValidation = validateCity(billingForm.city, touched.city);

  const postcodeRequired = currentCountry?.postcode != null;
  const postcodeValidation = {
    isValid: !postcodeRequired || billingForm.postcode.trim().length > 0,
    error: currentCountry?.postcode?.hint ?? "",
  };



  const handleBillingChange = (field: keyof typeof billingForm, value: string) => {
    setBillingForm((prev) => ({
      ...prev,
      [field]: value,
      // A region belongs to one country, so keeping the old one after a country change would
      // submit an address the server rejects.
      ...(field === "country" ? { state: "", postcode: "" } : {}),
    }));
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const handleBillingBlur = (field: keyof typeof billingForm) => {
    setTouched((prev) => ({ ...prev, [field]: true }));
  };

  const [quoteLoading, setQuoteLoading] = useState(false);
  const [quoteError, setQuoteError] = useState<string | null>(null);
  const [quote, setQuote] = useState<{
    plan: {
      name: string;
      currency: string;
      base_amount_ex_vat: string;
    };
    addon?: {
      package_id: string;
      name: string;
      credits: number;
      price: string;
      currency: string;
    };
    voucher?: {
      code: string;
    };
    charge?: {
      currency: string;
      total_payable: string;
    };
    bill: {
      vat_rate_percent: string;
      /** "VAT" in the Gulf, "Sales tax" in Pakistan — whichever gateway the country routes to. */
      tax_label?: string;
      discount_amount: string;
      subtotal_ex_vat: string;
      vat_amount: string;
      total_payable: string;
      total_payable_chargeable: boolean;
    };
  } | null>(null);

  const shareUrl = useMemo(() => {
    const params = new URLSearchParams();
    if (orgSlug) params.set("org", orgSlug);
    if (wsSlug) params.set("ws", wsSlug);
    if (billingForm.companyName) params.set("company", billingForm.companyName);
    if (billingForm.companyVatNumber) params.set("vat", billingForm.companyVatNumber);
    if (billingForm.companyCrNumber) params.set("cr", billingForm.companyCrNumber);
    if (billingForm.purchaseOrderNumber) params.set("po", billingForm.purchaseOrderNumber);
    if (includeCreditsAddon) params.set("addon", "bundle_100");
    const qs = params.toString() ? `?${params.toString()}` : "";
    return `${window.location.origin}${window.location.pathname}${qs}`;
  }, [orgSlug, wsSlug, billingForm, includeCreditsAddon]);

  const handleCopyShareUrl = () => {
    void navigator.clipboard.writeText(shareUrl);
    setShareCopied(true);
    setTimeout(() => setShareCopied(false), 2000);
  };

  /**
   * The whole bill is shown in the currency the plan was advertised in, and the SAR total is
   * disclosed once beneath it. Mixing the two through the line items would leave the buyer
   * adding up dollars to reach a riyal total.
   */
  const formatMoney = useCallback((amount: string, currency: string) => {
    const num = Number(amount);
    if (!Number.isFinite(num)) return `${currency} ${amount}`;
    return fmtDisplayPrice(num, currency).primary;
  }, []);

  /** The riyal figure that will appear on the statement, or null when it is already shown. */
  const charged = useMemo(() => {
    if (!quote?.charge) return null;
    const num = Number(quote.charge.total_payable);
    if (!Number.isFinite(num)) return null;
    return fmtCurrency(num, quote.charge.currency);
  }, [quote]);

  // A free order never reaches the gateway, so it has no OTP to send and no number to ask for.
  const phoneRequired = currentCountry?.phone != null && quote?.bill.total_payable_chargeable !== false;
  const phoneValidation = {
    isValid: !phoneRequired || isValidPakistaniMobile(billingForm.phone),
    error: billingForm.phone.trim() ? currentCountry?.phone?.hint ?? "" : "Required",
  };

  /** Named on the page so a Pakistani buyer is not told HyperPay is about to take their card. */
  const gatewayName = billingForm.country === "PK" ? "PayFast" : "HyperPay";

  const isBillingValid =
    phoneValidation.isValid &&
    validateName(billingForm.givenName, "First name", true).isValid &&
    validateName(billingForm.surname, "Last name", true).isValid &&
    validateEmail(billingForm.email, true).isValid &&
    validateAddress(billingForm.street1, true).isValid &&
    validateCity(billingForm.city, true).isValid &&
    postcodeValidation.isValid;

  const hasTax = quote ? Number(quote.bill.vat_rate_percent) > 0 : false;
  const taxLabel = quote?.bill.tax_label ?? "VAT";

  const fetchQuote = useCallback(
    async (voucherCode: string | null) => {
      setQuoteLoading(true);
      setQuoteError(null);
      setQuote(null);

      if (!isPlan && !isTier && !isPackage) {
        setQuoteError("Invalid plan, package, or pricing tier in the URL.");
        setQuoteLoading(false);
        return;
      }

      try {
        const { data: quoteData, error, response } = await api.calculatePaymentQuoteApiV1PaymentsQuotePost({
          body: {
            plan_id: isPlan ? Number(planId) : undefined,
            tier_id: isTier ? Number(tierId) : undefined,
            package_id: isPackage ? packageId : undefined,
            addon_package_id: isTier && includeCreditsAddon ? "bundle_100" : undefined,
            org_slug: orgSlug || undefined,
            voucher_code: voucherCode ?? undefined,
            country: billingForm.country,
            company_name: billingForm.companyName.trim() || undefined,
            company_vat_number: billingForm.companyVatNumber.trim() || undefined,
            company_cr_number: billingForm.companyCrNumber.trim() || undefined,
            purchase_order_number: billingForm.purchaseOrderNumber.trim() || undefined,
          } as any,
        });

        const data = (quoteData || error || {}) as { error?: string; detail?: string };

        if (response?.status === 401) {
          navigate("/sign-in");
          return;
        }

        if (error || !data) {
          throw new Error(data.error ?? data.detail ?? `Could not quote price (${response?.status}).`);
        }

        setQuote(data as any);
      } catch (e) {
        setQuoteError(e instanceof Error ? e.message : "Failed to load price. Please try again.");
      } finally {
        setQuoteLoading(false);
      }
    },
    [isPlan, isTier, isPackage, planId, tierId, packageId, orgSlug, navigate, billingForm.country, includeCreditsAddon],
  );

  const voucherInputInvalid =
    typeof quoteError === "string" && quoteError.toLowerCase().includes("voucher");

  const fetchCheckoutId = useCallback(
    async (voucherCode: string | null) => {
      setLoading(true);
      setError(null);
      setCheckoutId(null);

      if (!isPlan && !isTier && !isPackage) {
        setError("Invalid plan, package, or pricing tier in the URL.");
        setLoading(false);
        return;
      }

      const billingPayload = {
        given_name: billingForm.givenName.trim(),
        surname: billingForm.surname.trim(),
        email: billingForm.email.trim(),
        billing: {
          street1: billingForm.street1.trim(),
          city: billingForm.city.trim(),
          state: billingForm.state.trim(),
          country: billingForm.country.trim().toUpperCase(),
          postcode: billingForm.postcode.trim(),
        },
        ...(phoneRequired ? { phone: billingForm.phone.trim() } : {}),
        // PayFast needs absolute URLs to return the buyer and its notification to, and the API
        // is a separate service that does not know its own public address — the same reason
        // HyperPay's return URL below is built here from the client's base URL.
        return_base_url: (client.getConfig().baseUrl || window.location.origin).replace(/\/$/, ""),
      };

      // The address is part of the key: without it, correcting a rejected address would be
      // served the in-flight promise from the attempt that was rejected.
      const targetKey = isPackage ? `pkg:${packageId}` : (isTier ? `tier:${tierId}` : `plan:${planId}`);
      const requestKey = `${targetKey}::${voucherCode ?? ""}::${JSON.stringify(billingPayload)}`;
      let inflight = initiateCheckoutByPlanId.get(requestKey);
      if (!inflight) {
        inflight = (async () => {
          const { data: rawData, error, response } = await api.initiatePaymentApiV1PaymentsInitiatePost({
            body: {
              plan_id: isPlan ? Number(planId) : undefined,
              tier_id: isTier ? Number(tierId) : undefined,
              package_id: isPackage ? packageId : undefined,
              addon_package_id: isTier && includeCreditsAddon ? "bundle_100" : undefined,
              org_slug: orgSlug || undefined,
              workspace_slug: wsSlug || undefined,
              voucher_code: voucherCode ?? undefined,
              company_name: billingForm.companyName.trim() || undefined,
              company_vat_number: billingForm.companyVatNumber.trim() || undefined,
              company_cr_number: billingForm.companyCrNumber.trim() || undefined,
              purchase_order_number: billingForm.purchaseOrderNumber.trim() || undefined,
              ...billingPayload,
            } as any,
          });

          const data = (rawData || error || {}) as any;
          const redirectUrl = data.redirectUrl ?? data.redirect_url;
          const checkoutId = data.checkoutId ?? data.checkout_id;
          const payfast = data.payfast as PayFastHandoff | undefined;

          if (response?.status === 401) {
            navigate("/sign-in");
            return {};
          }

          if (error) {
            throw new Error(
              data.error ??
                data.detail ??
                `Could not start checkout (${response?.status}).`,
            );
          }

          if (redirectUrl) {
            return { redirectUrl };
          }

          if (payfast) {
            return { payfast };
          }

          if (!checkoutId) {
            throw new Error("No checkout session returned. Please try again.");
          }

          return { checkoutId };
        })();

        initiateCheckoutByPlanId.set(requestKey, inflight);
        void inflight.finally(() => {
          initiateCheckoutByPlanId.delete(requestKey);
        });
      }

      let leavingPage = false;
      try {
        const result = await inflight;
        if (result.redirectUrl) {
          leavingPage = true;
          const normalizedUrl = result.redirectUrl.replace(
            /^\/checkout\/success&/,
            "/checkout/success?",
          );
          navigate(normalizedUrl);
          return;
        }
        if (result.payfast) {
          // The browser is leaving for PayFast; `loading` stays on so the button cannot be
          // pressed again while the hosted page loads.
          leavingPage = true;
          submitToPayFast(result.payfast);
          return;
        }
        if (result.checkoutId) {
          setCheckoutId(result.checkoutId);
          return;
        }

        throw new Error("No checkout session returned. Please try again.");
      } catch (e) {
        if (e instanceof Error && e.message === "AUTH") {
          setError("Please sign in to continue.");
        } else {
          setError(
            e instanceof Error
              ? e.message
              : "Network error. Please check your connection and try again.",
          );
        }
      } finally {
        if (!leavingPage) setLoading(false);
      }
    },
    [isPlan, isTier, isPackage, planId, tierId, packageId, orgSlug, wsSlug, navigate, billingForm, phoneRequired],
  );

  useEffect(() => {
    void fetchQuote(appliedVoucherCode);
  }, [fetchQuote, appliedVoucherCode]);

  return (
    <main className="min-h-screen bg-[var(--background)] px-4 py-12 sm:py-16">
      <div className="mx-auto w-full max-w-lg">
        <div className="mb-8 text-center">
          <h1 className="text-2xl font-semibold tracking-tight text-[var(--foreground)] sm:text-3xl">
            Secure checkout
          </h1>
          <p className="mt-2 text-sm text-[var(--muted-foreground)]">
            Encrypted payment powered by {gatewayName}
          </p>
        </div>

        <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-6 shadow-sm sm:p-8">
          <div className="mb-6">
            <label className="block text-sm font-medium text-[var(--foreground)]">
              Voucher code (optional)
            </label>
            <div className="mt-2 flex gap-2">
              <input
                value={voucherInput}
                onChange={(e) => setVoucherInput(e.target.value)}
                placeholder="Enter code"
                className={[
                  "flex-1 rounded-lg border bg-[var(--card)] px-3 py-2 text-sm outline-none focus:ring-2",
                  voucherInputInvalid
                    ? "border-red-500 focus:ring-red-500"
                    : "border-[var(--border)] focus:ring-[var(--primary)]",
                ].join(" ")}
                autoComplete="off"
                disabled={quoteLoading || loading}
              />
              <button
                type="button"
                onClick={() => setAppliedVoucherCode(voucherInput.trim() ? voucherInput.trim() : null)}
                disabled={quoteLoading || loading}
                className="rounded-lg bg-[var(--primary)] px-4 py-2 text-sm font-medium text-[var(--primary-foreground)] transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 disabled:opacity-60"
              >
                Apply
              </button>
            </div>
            {appliedVoucherCode ? (
              <p className="mt-2 text-xs text-[var(--muted-foreground)]">
                Applied: <span className="font-semibold">{appliedVoucherCode}</span>
              </p>
            ) : (
              <p className="mt-2 text-xs text-[var(--muted-foreground)]">No voucher applied</p>
            )}
          </div>

          {quoteLoading && (
            <div
              className="flex flex-col items-center justify-center gap-4 py-16 text-center"
              aria-live="polite"
              aria-busy="true"
            >
              <Loader2
                className="h-10 w-10 text-[var(--primary)] animate-spin"
                aria-hidden
              />
              <p className="text-sm font-medium text-[var(--foreground)]">
                Loading your bill…
              </p>
              <p className="max-w-xs text-xs text-[var(--muted-foreground)]">
                Calculating totals (with your voucher if applied).
              </p>
            </div>
          )}

          {!quoteLoading && quoteError && (
            <div className="flex flex-col items-center gap-6 py-10 text-center">
              <p className="text-sm text-red-600 dark:text-red-400">{quoteError}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => void fetchQuote(appliedVoucherCode)}
                  className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-medium text-[var(--primary-foreground)] transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2"
                >
                  Try again
                </button>
                <Link
                  to="/courses"
                  className="text-sm font-medium text-[var(--primary)] underline-offset-4 hover:underline"
                >
                  Back to courses
                </Link>
              </div>
            </div>
          )}

          {!quoteLoading && quote && !checkoutId && !loading && (
            <div className="space-y-6">
              {/* Billing Information Form */}
              <div className="rounded-xl border border-[var(--border)] bg-[var(--card)] p-5 space-y-4">
                <div className="border-b border-[var(--border)] pb-3">
                  <h2 className="text-sm font-bold text-[var(--foreground)] flex items-center gap-2">
                    <ShieldCheck className="w-4 h-4 text-indigo-600" />
                    Billing & Customer Details
                  </h2>
                  <p className="text-xs text-[var(--muted-foreground)] mt-0.5">
                    Mandatory fields are marked with <span className="text-red-500 font-bold">*</span>.
                  </p>
                </div>

                {/* Name is split because HyperPay takes customer.givenName and
                    customer.surname separately, not one full name. */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    First Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Abdullah"
                    value={billingForm.givenName}
                    onChange={(e) => handleBillingChange("givenName", e.target.value)}
                    onBlur={() => handleBillingBlur("givenName")}
                    autoComplete="given-name"
                    className={`w-full px-3.5 py-2 rounded-lg border text-sm bg-[var(--card)] text-[var(--foreground)] outline-none transition ${
                      touched.givenName && !givenNameValidation.isValid
                        ? "border-red-500 bg-red-50/20 focus:ring-2 focus:ring-red-200"
                        : touched.givenName && givenNameValidation.isValid && billingForm.givenName
                        ? "border-emerald-500 bg-emerald-50/10 focus:ring-2 focus:ring-indigo-100"
                        : "border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]"
                    }`}
                  />
                  {touched.givenName && !givenNameValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {givenNameValidation.error}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    Last Name <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Al-Otaibi"
                    value={billingForm.surname}
                    onChange={(e) => handleBillingChange("surname", e.target.value)}
                    onBlur={() => handleBillingBlur("surname")}
                    autoComplete="family-name"
                    className={`w-full px-3.5 py-2 rounded-lg border text-sm bg-[var(--card)] text-[var(--foreground)] outline-none transition ${
                      touched.surname && !surnameValidation.isValid
                        ? "border-red-500 bg-red-50/20 focus:ring-2 focus:ring-red-200"
                        : touched.surname && surnameValidation.isValid && billingForm.surname
                        ? "border-emerald-500 bg-emerald-50/10 focus:ring-2 focus:ring-indigo-100"
                        : "border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]"
                    }`}
                  />
                  {touched.surname && !surnameValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {surnameValidation.error}
                    </p>
                  )}
                </div>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    Billing Email <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="email"
                    placeholder="e.g. name@domain.com"
                    value={billingForm.email}
                    onChange={(e) => handleBillingChange("email", e.target.value)}
                    onBlur={() => handleBillingBlur("email")}
                    autoComplete="email"
                    className={`w-full px-3.5 py-2 rounded-lg border text-sm bg-[var(--card)] text-[var(--foreground)] outline-none transition ${
                      touched.email && !emailValidation.isValid
                        ? "border-red-500 bg-red-50/20 focus:ring-2 focus:ring-red-200"
                        : touched.email && emailValidation.isValid && billingForm.email
                        ? "border-emerald-500 bg-emerald-50/10 focus:ring-2 focus:ring-indigo-100"
                        : "border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]"
                    }`}
                  />
                  {touched.email && !emailValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {emailValidation.error}
                    </p>
                  )}
                </div>

                {/* Country drives the region list and whether a postcode is asked for, so it
                    comes before both. */}
                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    Country / Region <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={billingForm.country}
                    onChange={(e) => handleBillingChange("country", e.target.value)}
                    onBlur={() => handleBillingBlur("country")}
                    disabled={countries == null}
                    className={"w-full px-3.5 py-2 rounded-lg border border-[var(--border)] text-sm bg-[var(--card)] text-[var(--foreground)] outline-none focus:ring-2 focus:ring-[var(--primary)] disabled:opacity-50"}
                  >
                    {(countries ?? []).map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.name}
                      </option>
                    ))}
                  </select>
                  {countries == null && (
                    <p className="text-[10px] text-[var(--muted-foreground)] mt-1">Loading countries…</p>
                  )}
                </div>

                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    Street Address <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. King Fahd Road, Apt 4B"
                    value={billingForm.street1}
                    onChange={(e) => handleBillingChange("street1", e.target.value)}
                    onBlur={() => handleBillingBlur("street1")}
                    autoComplete="street-address"
                    className={`w-full px-3.5 py-2 rounded-lg border text-sm bg-[var(--card)] text-[var(--foreground)] outline-none transition ${
                      touched.street1 && !addressValidation.isValid
                        ? "border-red-500 bg-red-50/20 focus:ring-2 focus:ring-red-200"
                        : touched.street1 && addressValidation.isValid && billingForm.street1
                        ? "border-emerald-500 bg-emerald-50/10 focus:ring-2 focus:ring-indigo-100"
                        : "border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]"
                    }`}
                  />
                  {touched.street1 && !addressValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {addressValidation.error}
                    </p>
                  )}
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                    City <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    placeholder="e.g. Riyadh"
                    value={billingForm.city}
                    onChange={(e) => handleBillingChange("city", e.target.value)}
                    onBlur={() => handleBillingBlur("city")}
                    autoComplete="address-level2"
                    className={`w-full px-3.5 py-2 rounded-lg border text-sm bg-[var(--card)] text-[var(--foreground)] outline-none transition ${
                      touched.city && !cityValidation.isValid
                        ? "border-red-500 bg-red-50/20 focus:ring-2 focus:ring-red-200"
                        : touched.city && cityValidation.isValid && billingForm.city
                        ? "border-emerald-500 bg-emerald-50/10 focus:ring-2 focus:ring-indigo-100"
                        : "border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]"
                    }`}
                  />
                  {touched.city && !cityValidation.isValid && (
                    <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                      <AlertCircle className="w-3 h-3 shrink-0" />
                      {cityValidation.error}
                    </p>
                  )}
                </div>

                  {/* Enumerated rather than free text: every country we sell to has a short,
                      fixed list, so there is nothing left for the buyer to get wrong. */}
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                      {currentCountry?.region_label ?? "Region"} <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={billingForm.state}
                      onChange={(e) => handleBillingChange("state", e.target.value)}
                      onBlur={() => handleBillingBlur("state")}
                      disabled={currentCountry == null}
                      className={"w-full px-3.5 py-2 rounded-lg border border-[var(--border)] text-sm bg-[var(--card)] text-[var(--foreground)] outline-none focus:ring-2 focus:ring-[var(--primary)] disabled:opacity-50"}
                    >
                      <option value="">
                        {currentCountry ? `Select ${currentCountry.region_label.toLowerCase()}` : "…"}
                      </option>
                      {(currentCountry?.regions ?? []).map((r) => (
                        <option key={r} value={r}>
                          {r}
                        </option>
                      ))}
                    </select>
                    {touched.state && !billingForm.state && (
                      <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        Required
                      </p>
                    )}
                  </div>
                </div>

                {/* Hidden entirely where the country has no postal system — the UAE and Qatar
                    do not use postcodes, and asking for one would block those buyers. */}
                {currentCountry?.postcode && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                      {currentCountry.postcode.label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="text"
                      placeholder={currentCountry.postcode.hint}
                      value={billingForm.postcode}
                      onChange={(e) => handleBillingChange("postcode", e.target.value)}
                      onBlur={() => handleBillingBlur("postcode")}
                      autoComplete="postal-code"
                      className="w-full px-3.5 py-2 rounded-lg border border-[var(--border)] text-sm bg-[var(--card)] text-[var(--foreground)] outline-none focus:ring-2 focus:ring-[var(--primary)]"
                    />
                    {touched.postcode && !postcodeValidation.isValid ? (
                      <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        Required
                      </p>
                    ) : (
                      <p className="text-[10px] text-[var(--muted-foreground)] mt-1">
                        Format: {currentCountry.postcode.hint}
                      </p>
                    )}
                  </div>
                )}

                {/* Only where the country's gateway needs it: PayFast sends wallet and bank OTPs
                    to this number, and HyperPay does not ask for one. */}
                {phoneRequired && currentCountry?.phone && (
                  <div>
                    <label className="block text-xs font-semibold text-[var(--foreground)] mb-1">
                      {currentCountry.phone.label} <span className="text-red-500">*</span>
                    </label>
                    <input
                      type="tel"
                      inputMode="tel"
                      placeholder={currentCountry.phone.hint}
                      value={billingForm.phone}
                      onChange={(e) => handleBillingChange("phone", e.target.value)}
                      onBlur={() => handleBillingBlur("phone")}
                      autoComplete="tel-national"
                      maxLength={15}
                      className={`w-full px-3.5 py-2 rounded-lg border text-sm bg-[var(--card)] text-[var(--foreground)] outline-none transition ${
                        touched.phone && !phoneValidation.isValid
                          ? "border-red-500 bg-red-50/20 focus:ring-2 focus:ring-red-200"
                          : touched.phone && phoneValidation.isValid && billingForm.phone
                          ? "border-emerald-500 bg-emerald-50/10 focus:ring-2 focus:ring-indigo-100"
                          : "border-[var(--border)] focus:ring-2 focus:ring-[var(--primary)]"
                      }`}
                    />
                    {touched.phone && !phoneValidation.isValid ? (
                      <p className="text-[11px] text-red-600 mt-1 flex items-center gap-1 font-medium">
                        <AlertCircle className="w-3 h-3 shrink-0" />
                        {phoneValidation.error}
                      </p>
                    ) : (
                      <p className="text-[10px] text-[var(--muted-foreground)] mt-1">
                        Your bank or wallet may send a one-time code to this number.
                      </p>
                    )}
                  </div>
                )}

                {/* B2B / Corporate Billing & Tax Information */}
                <div className="pt-2 border-t border-[var(--border)]">
                  <div className="flex items-center justify-between mb-2">
                    <label className="flex items-center gap-2 cursor-pointer select-none">
                      <input
                        type="checkbox"
                        checked={billingForm.isBusinessPurchase}
                        onChange={(e) =>
                          setBillingForm((prev) => ({ ...prev, isBusinessPurchase: e.target.checked }))
                        }
                        className="rounded border-[var(--border)] text-[var(--primary)] focus:ring-[var(--primary)] h-4 w-4"
                      />
                      <span className="text-xs font-semibold text-[var(--foreground)] flex items-center gap-1.5">
                        <Building2 className="w-3.5 h-3.5 text-indigo-500" />
                        This is a business or organizational purchase
                      </span>
                    </label>
                    {(isTier || isPackage) && (
                      <button
                        type="button"
                        onClick={() => setIsShareModalOpen(true)}
                        className="text-[11px] font-semibold text-indigo-600 hover:text-indigo-700 flex items-center gap-1 cursor-pointer"
                      >
                        <Share2 className="w-3 h-3" /> Share with Finance
                      </button>
                    )}
                  </div>

                  {billingForm.isBusinessPurchase && (
                    <div className="p-3.5 rounded-xl bg-slate-50 border border-slate-200/80 space-y-3 mt-2 animate-in fade-in duration-200">
                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            Company Legal Name
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. Acme Corp Ltd."
                            value={billingForm.companyName}
                            onChange={(e) => handleBillingChange("companyName" as any, e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            VAT / Tax ID (TRN)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. 311234567800003"
                            value={billingForm.companyVatNumber}
                            onChange={(e) => handleBillingChange("companyVatNumber" as any, e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>

                      <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            CR Number (KSA)
                          </label>
                          <input
                            type="text"
                            placeholder="10-digit Commercial Registration"
                            value={billingForm.companyCrNumber}
                            onChange={(e) => handleBillingChange("companyCrNumber" as any, e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                        <div>
                          <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-600 mb-1">
                            PO # / Cost Center (Optional)
                          </label>
                          <input
                            type="text"
                            placeholder="e.g. PO-2026-ENG-08"
                            value={billingForm.purchaseOrderNumber}
                            onChange={(e) => handleBillingChange("purchaseOrderNumber" as any, e.target.value)}
                            className="w-full px-3 py-1.5 rounded-lg border border-slate-200 bg-white text-xs font-medium text-slate-800 outline-none focus:ring-2 focus:ring-indigo-500"
                          />
                        </div>
                      </div>
                      <p className="text-[10px] text-slate-500">
                        These details will be formatted directly onto your official ZATCA-compliant Tax Invoice and payment receipt.
                      </p>
                    </div>
                  )}
                </div>
              </div>

              {/* B2B Add-on: AI Course Generator Credits Bundle */}
              {isTier && (
                <div className="rounded-xl border border-indigo-200 dark:border-indigo-900 bg-gradient-to-br from-indigo-50/70 to-purple-50/40 dark:from-indigo-950/20 dark:to-purple-950/10 p-4 transition">
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      id="addon-credits"
                      checked={includeCreditsAddon}
                      onChange={(e) => setIncludeCreditsAddon(e.target.checked)}
                      className="mt-1 h-4 w-4 rounded border-indigo-300 text-indigo-600 focus:ring-indigo-500 cursor-pointer"
                    />
                    <label htmlFor="addon-credits" className="cursor-pointer select-none">
                      <div className="flex items-center gap-2">
                        <span className="text-xs font-bold text-indigo-950 dark:text-indigo-200">
                          Bundle 100 AI Course Generation Credits
                        </span>
                        <span className="rounded-full bg-emerald-100 dark:bg-emerald-900/60 px-2 py-0.5 text-[10px] font-bold text-emerald-800 dark:text-emerald-300">
                          SAVE 25%
                        </span>
                      </div>
                      <p className="mt-1 text-xs text-indigo-900/80 dark:text-indigo-300/80 leading-relaxed">
                        Equip your team leaders with credits to instantly generate customized courses, flashcards, and quizzes for this workspace.
                      </p>
                      <p className="mt-1.5 text-xs font-semibold text-indigo-700 dark:text-indigo-400">
                        + $149.00 <span className="line-through text-indigo-400 dark:text-indigo-500 font-normal">$179.00</span>
                      </p>
                    </label>
                  </div>
                </div>
              )}

              {/* Order Summary */}
              <div className="rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
                <p className="text-sm font-semibold text-[var(--foreground)]">{quote.plan.name}</p>

                <div className="mt-4 space-y-3 text-sm">
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--muted-foreground)]">
                      Base price{hasTax ? ` (excl. ${taxLabel})` : ""}
                    </span>
                    <span className="font-medium">
                      {formatMoney(quote.plan.base_amount_ex_vat, quote.plan.currency)}
                    </span>
                  </div>
                  {quote.addon && (
                    <div className="flex items-center justify-between text-indigo-600 dark:text-indigo-400 font-medium">
                      <span className="flex items-center gap-1">
                        + Add-on: {quote.addon.name}
                      </span>
                      <span>
                        +{formatMoney(quote.addon.price, quote.addon.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--muted-foreground)]">
                      Voucher{quote.voucher ? ` (${quote.voucher.code})` : ""}
                    </span>
                    <span className="font-medium text-[var(--primary)]">
                      -{formatMoney(quote.bill.discount_amount, quote.plan.currency)}
                    </span>
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-[var(--muted-foreground)]">
                      Subtotal{hasTax ? ` (excl. ${taxLabel})` : ""}
                    </span>
                    <span className="font-medium">
                      {formatMoney(quote.bill.subtotal_ex_vat, quote.plan.currency)}
                    </span>
                  </div>
                  {hasTax && (
                    <div className="flex items-center justify-between">
                      <span className="text-[var(--muted-foreground)]">
                        {taxLabel} ({Number(quote.bill.vat_rate_percent)}%)
                      </span>
                      <span className="font-medium">
                        {formatMoney(quote.bill.vat_amount, quote.plan.currency)}
                      </span>
                    </div>
                  )}
                  <div className="flex items-center justify-between border-t pt-3">
                    <span className="text-[var(--muted-foreground)]">
                      Total payable{hasTax ? ` (incl. ${taxLabel})` : ""}
                    </span>
                    <span className="text-end text-base font-bold">
                      {formatMoney(quote.bill.total_payable, quote.plan.currency)}
                      {charged ? (
                        <span className="block text-xs font-normal text-[var(--muted-foreground)]">
                          Charged as {charged}
                        </span>
                      ) : null}
                    </span>
                  </div>
                </div>
              </div>

              <button
                type="button"
                onClick={() => {
                  setTouched({
                    name: true,
                    email: true,
                    country: true,
                    address: true,
                    city: true,
                    postalCode: true,
                  });
                  if (isBillingValid) {
                    void fetchCheckoutId(appliedVoucherCode);
                  }
                }}
                disabled={quoteLoading || loading || !isBillingValid}
                className="w-full rounded-xl bg-[var(--primary)] py-3 text-sm font-semibold text-[var(--primary-foreground)] shadow-sm transition hover:opacity-90 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading
                  ? "Starting payment…"
                  : quote.bill.total_payable_chargeable
                    ? "Continue to secure checkout"
                    : "Complete purchase"}
              </button>

              <p className="text-center text-xs text-[var(--muted-foreground)]">
                Encrypted payment powered by {gatewayName}
              </p>
            </div>
          )}

          {!quoteLoading && loading && !checkoutId && !error && (
            <div className="flex flex-col items-center gap-4 py-10 text-center" aria-live="polite" aria-busy="true">
              <Loader2 className="h-8 w-8 animate-spin text-[var(--primary)]" aria-hidden />
              <p className="text-sm text-[var(--foreground)]">
                {gatewayName === "PayFast" ? "Taking you to PayFast…" : "Securing your checkout…"}
              </p>
            </div>
          )}

          {!quoteLoading && !loading && error && (
            <div className="flex flex-col items-center gap-6 py-10 text-center">
              <p className="text-sm text-red-600 dark:text-red-400">{error}</p>
              <div className="flex flex-wrap items-center justify-center gap-3">
                <button
                  type="button"
                  onClick={() => void fetchCheckoutId(appliedVoucherCode)}
                  className="rounded-lg bg-[var(--primary)] px-5 py-2.5 text-sm font-medium text-[var(--primary-foreground)] transition hover:opacity-90 focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2"
                >
                  Try again
                </button>
                <Link
                  to={orgSlug ? `/org/${orgSlug}/dashboard` : "/courses"}
                  className="text-sm font-medium text-[var(--primary)] underline-offset-4 hover:underline"
                >
                  {orgSlug ? "Back to dashboard" : "Back to courses"}
                </Link>
              </div>
            </div>
          )}

          {!quoteLoading && !error && checkoutId && (
            <div className="space-y-6">
              <p className="text-center text-sm text-[var(--muted-foreground)]">
                Enter your card details below to complete your purchase.
              </p>
              <form
                action={`${client.getConfig().baseUrl || ""}/api/v1/payments/verify`}
                className="paymentWidgets"
                data-brands="VISA MASTER MADA"
              />
              <Script
                key={checkoutId}
                src={`${PAYMENT_WIDGET_SCRIPT_BASE}?checkoutId=${encodeURIComponent(checkoutId)}`}
                strategy="afterInteractive"
              />
            </div>
          )}
        </div>
      </div>

      {/* Share with Finance / Corporate Approver Modal */}
      {isShareModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-in fade-in duration-150">
          <div className="relative w-full max-w-lg rounded-2xl bg-white p-6 shadow-2xl border border-slate-200">
            <div className="flex items-center justify-between pb-3 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <Share2 className="w-5 h-5 text-indigo-600" />
                <h3 className="text-base font-bold text-slate-900">Share Requisition with Finance</h3>
              </div>
              <button
                type="button"
                onClick={() => setIsShareModalOpen(false)}
                className="rounded-lg p-1 text-slate-400 hover:bg-slate-100 hover:text-slate-600"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="mt-4 space-y-4 text-xs text-slate-600">
              <p>
                Need corporate approval or card payment from your procurement team? Share this direct, pre-configured link containing your organization, PO, and corporate tax information.
              </p>

              <div className="p-3 rounded-xl bg-slate-50 border border-slate-200/80 space-y-1.5">
                <div className="flex justify-between">
                  <span className="text-slate-500">Plan / Item:</span>
                  <span className="font-semibold text-slate-800">{quote?.plan.name || "Course Tier"}</span>
                </div>
                {quote?.addon && (
                  <div className="flex justify-between text-indigo-600">
                    <span>Add-on:</span>
                    <span className="font-medium">{quote.addon.name}</span>
                  </div>
                )}
                {billingForm.companyName && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">Company:</span>
                    <span className="font-medium text-slate-800">{billingForm.companyName}</span>
                  </div>
                )}
                {billingForm.purchaseOrderNumber && (
                  <div className="flex justify-between">
                    <span className="text-slate-500">PO #:</span>
                    <span className="font-mono font-medium text-slate-800">{billingForm.purchaseOrderNumber}</span>
                  </div>
                )}
                <div className="flex justify-between border-t border-slate-200/60 pt-1.5 font-bold text-slate-900">
                  <span>Total Estimated:</span>
                  <span className="text-indigo-700 font-mono text-sm">
                    {quote ? formatMoney(quote.bill.total_payable, quote.plan.currency) : "—"}
                  </span>
                </div>
              </div>

              <div>
                <label className="block text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1">
                  Pre-Configured Checkout Link
                </label>
                <div className="flex items-center gap-2">
                  <input
                    type="text"
                    readOnly
                    value={shareUrl}
                    className="w-full px-3 py-2 rounded-lg border border-slate-200 bg-slate-50 text-xs font-mono text-slate-700 select-all outline-none"
                  />
                  <button
                    type="button"
                    onClick={handleCopyShareUrl}
                    className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg bg-indigo-600 text-white font-semibold hover:bg-indigo-700 transition shrink-0"
                  >
                    {shareCopied ? (
                      <>
                        <Check className="w-3.5 h-3.5 text-emerald-300" />
                        Copied
                      </>
                    ) : (
                      <>
                        <Copy className="w-3.5 h-3.5" />
                        Copy
                      </>
                    )}
                  </button>
                </div>
              </div>

              <div className="pt-2 flex items-center justify-between">
                <a
                  href={`mailto:?subject=${encodeURIComponent(`[Payment Requisition] ${quote?.plan.name || "Inara Course Access"}`)}&body=${encodeURIComponent(
                    `Hi Finance Team,\n\nPlease complete the payment checkout for our team:\n\n• Item: ${quote?.plan.name || ""}\n• Total: ${quote?.bill.total_payable || ""} ${quote?.plan.currency || ""}\n• PO #: ${billingForm.purchaseOrderNumber || "N/A"}\n\nPayment Link:\n${shareUrl}\n\nThank you!`
                  )}`}
                  className="text-xs font-semibold text-indigo-600 hover:text-indigo-700 underline underline-offset-4"
                >
                  Draft email in your mail client &rarr;
                </a>
                <button
                  type="button"
                  onClick={() => setIsShareModalOpen(false)}
                  className="px-4 py-2 rounded-lg border border-slate-200 text-xs font-semibold text-slate-700 hover:bg-slate-50"
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </main>
  );
}
